import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  listTemplates,
  sendTemplateMessage,
  getWhatsAppErrorMessage,
  unwrapWhatsAppList,
} from "../../../services/whatsappApi";

export default function SendTemplateModal({ show, onHide, chatId, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await listTemplates({ status: "APPROVED", limit: 10 });
        if (!cancelled) {
          const rows = unwrapWhatsAppList(res);
          setTemplates(rows);
          if (rows.length && !templateName) {
            const first = rows[0];
            setTemplateName(first.name || first.template_name || "");
            setLanguage(first.language || first.template_language || "en");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(getWhatsAppErrorMessage(err, "Failed to load templates"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleTemplateChange = (name) => {
    setTemplateName(name);
    const match = templates.find(
      (t) => (t.name || t.template_name) === name,
    );
    if (match) {
      setLanguage(match.language || match.template_language || "en");
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!chatId || !templateName) {
      setError("Select a template");
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await sendTemplateMessage(chatId, {
        template_name: templateName,
        template_language: language || "en",
        body_variables: [],
      });
      if (res?.success === false) {
        throw new Error(res.message || "Failed to send template");
      }
      toast.success("Template sent");
      onHide();
      if (typeof onSent === "function") onSent(res);
    } catch (err) {
      const msg = getWhatsAppErrorMessage(err, "Failed to send template");
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSend}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon icon="solar:letter-line-duotone" width={20} className="me-1" />
            Send template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error ? <div className="alert alert-danger py-2">{error}</div> : null}
          <Form.Group className="mb-3">
            <Form.Label>Approved template *</Form.Label>
            <Form.Select
              value={templateName}
              onChange={(e) => handleTemplateChange(e.target.value)}
              disabled={loading || sending}
              required
            >
              <option value="">Select template</option>
              {templates.map((tpl) => {
                const name = tpl.name || tpl.template_name || tpl.id;
                const lang = tpl.language || tpl.template_language || "en";
                return (
                  <option key={`${name}-${lang}`} value={name}>
                    {name} ({lang})
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Language</Form.Label>
            <Form.Control
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={sending}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={sending}>
            Cancel
          </Button>
          <Button type="submit" variant="success" disabled={sending || loading}>
            {sending ? "Sending..." : "Send template"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
